import * as _ from 'lodash';
import useSWR from 'swr';
import mondaySdk from 'monday-sdk-js';
import { AttentionBox, Box, Divider, Flex } from '@vibe/core';
import { Board } from '@gorilla/common/src/lib/monday-api/api';
import {
  createMondayClient,
  createMondaySdkRequester,
  extractApiResult,
  MondayRequesterError,
} from '@gorilla/common/src/lib/monday-api/common';

// `hierarchy_type` doesn't exist in the API version the rest of the app uses (2025-07, it was introduced in 2025-10),
// so it's fetched with a separate SDK instance pinned to a newer version
const mondayWithHierarchyType = mondaySdk();
mondayWithHierarchyType.setApiVersion('2026-01');
const mondayClientWithHierarchyType = createMondayClient(createMondaySdkRequester(mondayWithHierarchyType));

async function getMultiLevelBoardIds(boardIds: number[]) {
  const result = await mondayClientWithHierarchyType.query(
    `
    query getBoardHierarchyTypes($boardIds: [ID!]!) {
      complexity {
        query
      }
      boards(ids: $boardIds, limit: 100) {
        id
        hierarchy_type
      }
    }
  `,
    { boardIds },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  const boards = extractApiResult<{ id: string; hierarchy_type: 'classic' | 'multi_level' | null }[]>(result, 'boards');

  return boards.filter((board) => board.hierarchy_type === 'multi_level').map((board) => _.toNumber(board.id));
}

type MultiLevelBoardWarningProps = {
  boardIds: number[];
  boards: Board[];
};

export function MultiLevelBoardWarning({ boardIds, boards }: MultiLevelBoardWarningProps) {
  const uniqueBoardIds = _.sortBy(_.uniq(boardIds));

  // if the hierarchy types can't be fetched, no warning is shown
  const { data: multiLevelBoardIds } = useSWR(
    uniqueBoardIds.length ? `/multi-level-board-ids/${uniqueBoardIds.join('/')}` : null,
    () => getMultiLevelBoardIds(uniqueBoardIds),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  if (!multiLevelBoardIds || !multiLevelBoardIds.length) {
    return null;
  }

  const boardNames = multiLevelBoardIds.map((boardId) => {
    const board = boards.find((board) => board.id === boardId);

    return `"${board ? board.name : `Board ${boardId}`}"`;
  });

  return (
    <>
      <Box padding="large">
        <Flex justify="center" align="center" style={{ width: '100%' }}>
          <AttentionBox
            type="warning"
            text={`Multi-level boards aren't supported yet. Data from ${boardNames.join(', ')} may be incomplete or wrong in this spreadsheet.`}
          />
        </Flex>
      </Box>
      <Divider withoutMargin />
    </>
  );
}
